import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  AUTHORITY_OPTIONS,
  authorityToFormState,
  authorityFromFormState,
} from "@/lib/authorities";
import {
  INSPECTION_STATUSES,
  INSPECTION_PENALTIES,
  INSPECTION_TRIGGERS,
  getInspectionStatus,
} from "@/lib/inspections";

const emptyInspection = {
  authoritySelect: "",
  authorityCustom: "",
  inspector_name: "",
  inspection_date: "",
  status: "new",
  subject: "",
  findings: "",
  follow_up_date: "",
  notes: "",
  penalty: "",
  penalty_amount: "",
  penalty_reason: "",
  triggered_by_report: "",
};

export function InspectionFormDialog({
  open,
  onOpenChange,
  siteId,
  inspection,
  onSaved,
}) {
  const [form, setForm] = useState(emptyInspection);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    if (inspection) {
      const a = authorityToFormState(inspection.authority);
      setForm({
        authoritySelect: a.select,
        authorityCustom: a.custom,
        inspector_name: inspection.inspector_name || "",
        inspection_date: inspection.inspection_date || "",
        status: getInspectionStatus(inspection),
        subject: inspection.subject || "",
        findings: inspection.findings || "",
        follow_up_date: inspection.follow_up_date || "",
        notes: inspection.notes || "",
        penalty: inspection.penalty || "",
        penalty_amount:
          inspection.penalty_amount != null ? String(inspection.penalty_amount) : "",
        penalty_reason: inspection.penalty_reason || "",
        triggered_by_report: inspection.triggered_by_report || "",
      });
    } else {
      setForm(emptyInspection);
    }
    setErr("");
  }, [open, inspection]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const authority = authorityFromFormState({
        select: form.authoritySelect,
        custom: form.authorityCustom,
      });
      if (!authority) {
        setErr("Válassz hatóságot a listából, vagy add meg a nevét.");
        setSaving(false);
        return;
      }
      // Büntetés validáció kliens oldalon
      if (form.penalty === "had_penalty") {
        const amt = parseFloat(form.penalty_amount);
        if (!amt || amt <= 0) {
          setErr("Büntetés esetén kötelező megadni az összeget.");
          setSaving(false);
          return;
        }
        if (!form.penalty_reason.trim()) {
          setErr("Büntetés esetén kötelező megadni az indokot.");
          setSaving(false);
          return;
        }
      }
      const payload = {
        authority,
        inspector_name: form.inspector_name || null,
        inspection_date: form.inspection_date,
        status: form.status || "new",
        subject: form.subject || null,
        findings: form.findings || null,
        follow_up_date: form.follow_up_date || null,
        notes: form.notes || null,
        penalty: form.penalty || null,
        penalty_amount:
          form.penalty === "had_penalty" ? parseFloat(form.penalty_amount) : null,
        penalty_reason:
          form.penalty === "had_penalty" ? form.penalty_reason.trim() || null : null,
        triggered_by_report: form.triggered_by_report || null,
      };
      // Lezárt ellenőrzésnél a záráskor rögzített mezőket nem küldjük el patch-re
      // (a backend 409-cel utasítaná el). A státuszt sem mozgatjuk.
      if (inspection && inspection.status === "closed") {
        delete payload.status;
        delete payload.penalty;
        delete payload.penalty_amount;
        delete payload.penalty_reason;
      }
      let saved;
      if (inspection) {
        const { data } = await api.patch(`/inspections/${inspection._id}`, payload);
        saved = data;
        toast.success("Ellenőrzés frissítve.");
      } else {
        if (!siteId) {
          throw new Error("Hiányzó telephely.");
        }
        const { data } = await api.post(`/sites/${siteId}/inspections`, payload);
        saved = data;
        toast.success("Ellenőrzés létrehozva.");
      }
      onOpenChange(false);
      if (onSaved) onSaved(saved);
    } catch (error) {
      setErr(formatApiError(error?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const showCustomAuthority = form.authoritySelect === "other";
  const showPenaltyDetails = form.penalty === "had_penalty";
  const isClosed = inspection && (inspection.status === "closed");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="inspection-dialog"
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {inspection ? "Ellenőrzés szerkesztése" : "Új hatósági ellenőrzés"}
          </DialogTitle>
          <DialogDescription>
            Rögzítsd a hatósági ellenőrzés részleteit.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField label="Hatóság *" testId="insp-form-authority">
            <Select
              value={form.authoritySelect}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  authoritySelect: v,
                  authorityCustom: v === "other" ? f.authorityCustom : "",
                }))
              }
            >
              <SelectTrigger data-testid="insp-form-authority-select">
                <SelectValue placeholder="Válassz hatóságot…" />
              </SelectTrigger>
              <SelectContent>
                {AUTHORITY_OPTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          {showCustomAuthority && (
            <FormField label="Hatóság megnevezése *" testId="insp-form-authority-custom">
              <Input
                required
                value={form.authorityCustom}
                onChange={(e) =>
                  setForm((f) => ({ ...f, authorityCustom: e.target.value }))
                }
                placeholder="Add meg a hatóság pontos nevét"
              />
            </FormField>
          )}
          <FormField label="Státusz" testId="insp-form-status">
            <Select
              value={form.status}
              onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              disabled={isClosed}
            >
              <SelectTrigger data-testid="insp-form-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INSPECTION_STATUSES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isClosed && (
              <p className="text-[10.5px] text-muted-foreground">
                Lezárt ellenőrzés státusza nem módosítható.
              </p>
            )}
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Dátum *" testId="insp-form-date">
              <DateInput
                value={form.inspection_date}
                onChange={(v) =>
                  setForm((f) => ({ ...f, inspection_date: v }))
                }
              />
            </FormField>
            <FormField label="Ellenőr neve" testId="insp-form-inspector">
              <Input
                value={form.inspector_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, inspector_name: e.target.value }))
                }
                placeholder="Nagy Béla"
              />
            </FormField>
          </div>
          <FormField label="Tárgya" testId="insp-form-subject">
            <Input
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="Pl. Élelmiszerbiztonsági ellenőrzés"
            />
          </FormField>

          {/* Büntetés */}
          <FormField label="Büntetés" testId="insp-form-penalty">
            <Select
              value={form.penalty || "__none__"}
              disabled={isClosed}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  penalty: v === "__none__" ? "" : v,
                  // Ha nem büntetés -> tisztítsuk a részleteket
                  penalty_amount: v === "had_penalty" ? f.penalty_amount : "",
                  penalty_reason: v === "had_penalty" ? f.penalty_reason : "",
                }))
              }
            >
              <SelectTrigger data-testid="insp-form-penalty-select">
                <SelectValue placeholder="Válassz…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nincs megadva</SelectItem>
                {INSPECTION_PENALTIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isClosed && (
              <p className="text-[10.5px] text-muted-foreground">
                Lezáráskor rögzített, nem szerkeszthető.
              </p>
            )}
          </FormField>
          {showPenaltyDetails && (
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-rose-200 bg-rose-50/40 p-3 dark:border-rose-500/20 dark:bg-rose-500/5">
              <FormField label="Büntetés összege (Ft) *" testId="insp-form-penalty-amount">
                <Input
                  type="number"
                  min="0"
                  step="1000"
                  value={form.penalty_amount}
                  disabled={isClosed}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, penalty_amount: e.target.value }))
                  }
                  placeholder="Pl. 150000"
                  data-testid="insp-form-penalty-amount-input"
                />
              </FormField>
              <FormField label="Büntetés indoka *" testId="insp-form-penalty-reason">
                <Textarea
                  value={form.penalty_reason}
                  disabled={isClosed}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, penalty_reason: e.target.value }))
                  }
                  rows={2}
                  placeholder="Pl. Élelmiszer-higiéniai szabályok megsértése"
                  data-testid="insp-form-penalty-reason-input"
                />
              </FormField>
            </div>
          )}

          {/* Bejelentés alapján volt-e az ellenőrzés */}
          <FormField
            label="Bejelentés alapján volt-e az ellenőrzés?"
            testId="insp-form-triggered"
          >
            <Select
              value={form.triggered_by_report || "__none__"}
              onValueChange={(v) =>
                setForm((f) => ({
                  ...f,
                  triggered_by_report: v === "__none__" ? "" : v,
                }))
              }
            >
              <SelectTrigger data-testid="insp-form-triggered-select">
                <SelectValue placeholder="Válassz…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nincs megadva</SelectItem>
                {INSPECTION_TRIGGERS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField label="Megállapítások" testId="insp-form-findings">
            <Textarea
              value={form.findings}
              onChange={(e) =>
                setForm((f) => ({ ...f, findings: e.target.value }))
              }
              rows={3}
              placeholder="A hatóság észrevételei, megállapítások…"
            />
          </FormField>
          <FormField label="Utólagos teendő határideje" testId="insp-form-follow-up">
            <DateInput
              value={form.follow_up_date}
              onChange={(v) =>
                setForm((f) => ({ ...f, follow_up_date: v }))
              }
            />
          </FormField>
          <FormField label="Megjegyzés" testId="insp-form-notes">
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
            />
          </FormField>
          {err && (
            <div
              data-testid="inspection-form-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-inspection"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-inspection">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : inspection ? (
                "Mentés"
              ) : (
                "Létrehozás"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
